import { useEffect } from "react";

// Each setter applies a value and returns a restore() that reverts the head to
// its prior state (restores previous content, or removes the tag we created).
function setMeta(attr, key, content) {
  if (content == null || content === "") return () => {};
  const el0 = document.head.querySelector(`meta[${attr}="${key}"]`);
  const existed = !!el0;
  const prev = existed ? el0.getAttribute("content") : null;
  const el = el0 || document.createElement("meta");
  if (!existed) {
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (existed) el.setAttribute("content", prev ?? "");
    else if (el.parentNode) el.parentNode.removeChild(el);
  };
}

function setLink(rel, href) {
  if (!href) return () => {};
  const el0 = document.head.querySelector(`link[rel="${rel}"]`);
  const existed = !!el0;
  const prev = existed ? el0.getAttribute("href") : null;
  const el = el0 || document.createElement("link");
  if (!existed) {
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => {
    if (existed) el.setAttribute("href", prev ?? "");
    else if (el.parentNode) el.parentNode.removeChild(el);
  };
}

function setJsonLd(id, jsonString) {
  if (!jsonString) return () => {};
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.id = id;
  el.textContent = jsonString;
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  document.head.appendChild(el);
  return () => {
    if (el.parentNode) el.parentNode.removeChild(el);
  };
}

export function truncate(text, max = 158) {
  if (!text) return "";
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${cut.slice(0, lastSpace > 60 ? lastSpace : max).trim()}…`;
}

/**
 * Dynamically manages per-page SEO tags (title, description, Open Graph,
 * Twitter, canonical, JSON-LD) and fully restores the previous head state on
 * unmount. Google renders JS and indexes these. Renders nothing.
 */
export function Seo({ title, description, image, url, type = "website", jsonLd }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    const restores = [
      setMeta("name", "description", description),
      setMeta("property", "og:title", title),
      setMeta("property", "og:description", description),
      setMeta("property", "og:type", type),
      setMeta("property", "og:image", image),
      setMeta("property", "og:url", url),
      setMeta("name", "twitter:card", image ? "summary_large_image" : "summary"),
      setMeta("name", "twitter:title", title),
      setMeta("name", "twitter:description", description),
      setMeta("name", "twitter:image", image),
      setLink("canonical", url),
      setJsonLd("ld-page", jsonLd),
    ];
    return () => {
      document.title = prevTitle;
      restores.forEach((r) => r());
    };
  }, [title, description, image, url, type, jsonLd]);
  return null;
}
